export const METRONOME_MIN_BPM = 40;
export const METRONOME_MAX_BPM = 220;

export const METRONOME_SOUND_VALUES = [
  "metronome",
  "drumstick",
  "tincan",
  "woodblock",
] as const;

export type MetronomeSound = (typeof METRONOME_SOUND_VALUES)[number];
export type MetronomeTockSound = MetronomeSound | "none";

export interface MetronomeConfig {
  speed: number;
  sound_tick: MetronomeSound;
  sound_tock: MetronomeTockSound;
  tock_every: number;
  peripheral: boolean;
  allow_customized_bpm: boolean;
  allow_customized_sound: boolean;
  bpm_range: {
    min: number;
    max: number;
  };
}

export type MetronomeConfigValidationError =
  | "invalid_speed"
  | "invalid_sound"
  | "invalid_tock_every"
  | "invalid_bpm_range"
  | "speed_outside_bpm_range";

export function createDefaultMetronomeConfig(): MetronomeConfig {
  return {
    speed: 60,
    sound_tick: "metronome",
    sound_tock: "woodblock",
    tock_every: 4,
    peripheral: false,
    allow_customized_bpm: false,
    allow_customized_sound: false,
    bpm_range: {
      min: METRONOME_MIN_BPM,
      max: METRONOME_MAX_BPM,
    },
  };
}

export function isMetronomeSound(value: unknown): value is MetronomeSound {
  return METRONOME_SOUND_VALUES.includes(value as MetronomeSound);
}

export function isMetronomeTockSound(
  value: unknown
): value is MetronomeTockSound {
  return value === "none" || isMetronomeSound(value);
}

export function clampMetronomeBpm(value: unknown, fallback = 60): number {
  const parsed = Number(value);
  const bpm = Number.isFinite(parsed) ? Math.round(parsed) : fallback;
  return Math.min(METRONOME_MAX_BPM, Math.max(METRONOME_MIN_BPM, bpm));
}

export function normalizeMetronomeConfig(
  value: Partial<MetronomeConfig> | null | undefined
): MetronomeConfig {
  const defaults = createDefaultMetronomeConfig();
  const rawMin = clampMetronomeBpm(value?.bpm_range?.min, defaults.bpm_range.min);
  const rawMax = clampMetronomeBpm(value?.bpm_range?.max, defaults.bpm_range.max);
  const min = Math.min(rawMin, rawMax);
  const max = Math.max(rawMin, rawMax);
  const allowCustomizedBpm = value?.allow_customized_bpm === true;
  let speed = clampMetronomeBpm(value?.speed, defaults.speed);

  if (allowCustomizedBpm) {
    speed = Math.min(max, Math.max(min, speed));
  }

  return {
    speed,
    sound_tick: isMetronomeSound(value?.sound_tick)
      ? value.sound_tick
      : defaults.sound_tick,
    sound_tock: isMetronomeTockSound(value?.sound_tock)
      ? value.sound_tock
      : defaults.sound_tock,
    tock_every: Math.min(
      100,
      Math.max(1, Math.round(Number(value?.tock_every) || defaults.tock_every))
    ),
    peripheral: value?.peripheral === true,
    allow_customized_bpm: allowCustomizedBpm,
    allow_customized_sound: value?.allow_customized_sound === true,
    bpm_range: { min, max },
  };
}

export function validateMetronomeConfig(
  value: Partial<MetronomeConfig> | null | undefined
): MetronomeConfigValidationError[] {
  if (!value) return ["invalid_speed", "invalid_sound", "invalid_bpm_range"];

  const errors: MetronomeConfigValidationError[] = [];
  const speed = Number(value.speed);
  const min = Number(value.bpm_range?.min);
  const max = Number(value.bpm_range?.max);
  const validSpeed =
    Number.isInteger(speed) && speed >= METRONOME_MIN_BPM && speed <= METRONOME_MAX_BPM;
  const validRange =
    Number.isInteger(min) &&
    Number.isInteger(max) &&
    min >= METRONOME_MIN_BPM &&
    max <= METRONOME_MAX_BPM &&
    min <= max;

  if (!validSpeed) errors.push("invalid_speed");
  if (
    !isMetronomeSound(value.sound_tick) ||
    !isMetronomeTockSound(value.sound_tock)
  ) {
    errors.push("invalid_sound");
  }
  if (
    !Number.isInteger(Number(value.tock_every)) ||
    Number(value.tock_every) < 1 ||
    Number(value.tock_every) > 100
  ) {
    errors.push("invalid_tock_every");
  }
  if (!validRange) errors.push("invalid_bpm_range");
  if (
    validSpeed &&
    validRange &&
    value.allow_customized_bpm === true &&
    (speed < min || speed > max)
  ) {
    errors.push("speed_outside_bpm_range");
  }

  return errors;
}
